'use client';

import { useRef } from 'react';
import { Image as ImageIcon, Loader2, Upload } from 'lucide-react';

import { useUploadImage } from '@/lib/api/images';

const IMAGE_ACCEPT = 'image/jpeg,image/jpg,image/png,image/gif,image/webp';

interface BulkEditThumbUploadProps {
  imageUrl: string | null;
  disabled?: boolean;
  onImageAvailable: (url: string) => void;
}

export function BulkEditThumbUpload({
  imageUrl,
  disabled = false,
  onImageAvailable,
}: BulkEditThumbUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const { mutate: uploadImage, isPending } = useUploadImage();

  const handleFile = (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    uploadImage(formData, {
      onSuccess: (data) => onImageAvailable(data.file.url),
    });
  };

  return (
    <>
      <button
        type="button"
        disabled={disabled || isPending}
        onClick={() => {
          if (!disabled && !isPending) inputRef.current?.click();
        }}
        className={[
          'crm-bulk-edit-thumb',
          'group',
          'relative',
          'shrink-0',
          disabled ? null : 'cursor-pointer',
        ]
          .filter(Boolean)
          .join(' ')}
        aria-label="Upload image"
        aria-busy={isPending}
      >
        {imageUrl ? (
          <img src={imageUrl} alt="" className="size-full object-cover" />
        ) : (
          <ImageIcon className="size-3.5 shrink-0 text-muted-foreground/50" aria-hidden />
        )}

        {isPending ? (
          <span
            className="crm-bulk-edit-thumb__overlay crm-bulk-edit-thumb__overlay--loading"
            aria-hidden
          >
            <Loader2 className="size-3.5 shrink-0 animate-spin text-muted-foreground" />
          </span>
        ) : !disabled ? (
          <span
            className="crm-bulk-edit-thumb__overlay crm-bulk-edit-thumb__overlay--upload"
            aria-hidden
          >
            <Upload className="size-3.5 shrink-0 text-white" />
          </span>
        ) : null}
      </button>
      {!disabled ? (
        <input
          ref={inputRef}
          type="file"
          accept={IMAGE_ACCEPT}
          className="sr-only"
          tabIndex={-1}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleFile(file);
            e.target.value = '';
          }}
        />
      ) : null}
    </>
  );
}
